import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseUrl, batchSize, heightSelectionList } from "../comman/config";
import Select from "react-select";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";
import { DateSessionFilter } from "../components/Filter/DateSessionFilter";
import toast, { Toaster } from 'react-hot-toast';
import { ItemNameStacked } from '../components/ItemName';
import { menuAboveTable, withMenuOnTop } from "../comman/selectMenu";

// The three measurement families this screen records, each under two method ids.
const KGS = [1, 4];
const LTR = [2, 5];
const NOS = [3, 6];
const familyOf = (method) =>
  KGS.includes(Number(method)) ? 'kgs' :
  LTR.includes(Number(method)) ? 'ltr' :
  NOS.includes(Number(method)) ? 'nos' : null;

// The bowl a batch starts on when nothing has been recorded for it yet.
const DEFAULT_BOWL = { kgs: 'M-1', ltr: 'M-1', nos: 'I-1' };
// ... and the number of bowls those batches are measured over.
const DEFAULT_BOWL_COUNT = 3;

// Every batch column is built from one of these lists, and so is the column of labels down the
// left: a field's label, its place in the order and whether it can be typed into are stated once,
// so the two columns cannot drift apart. The label is also the box's placeholder -- the help text
// and the label are the same words.
//
//   calc: worked out from the other fields, never typed into
//   hidden: kept in the order it belongs in, but not drawn -- see Bottom Dia below
const FIELDS = {
  kgs: [
    { name: 'filled_weight',               label: 'Gross Weight (kgs)' },
    { name: 'no_of_bowls_for_measurement', label: 'Total Bowls (nos)' },
    { name: 'weight_of_1_empty_bowl',      label: '1 Bowl Empty (kgs)' },
    { name: 'total_empty_weight',          label: 'Empty Weight (kgs)', calc: true },
    { name: 'net_fg_weight',               label: 'Net Weight (kgs)', calc: true },
    { name: 'remarks_kgs',                 label: 'Remarks', type: 'text' },
  ],
  ltr: [
    { name: 'diameter_of_filled_height_d2', label: 'Filled Height Dia (cms)' },
    { name: 'height_selection',             label: 'Height Selection', type: 'select', options: heightSelectionList },
    { name: 'height',                       label: 'Height (cms)' },
    // Hidden for now. It belongs here -- before Sensor Volume -- for the day it is wanted back,
    // and the volume no longer reads it: a bowl is measured on its filled-height diameter alone.
    { name: 'bottom_diameter_d1',           label: 'Bottom Dia (cms)', calc: true, hidden: true },
    { name: 'sensor_volume',                label: 'Sensor Volume' },
    { name: 'manual_volume',                label: 'Manual Volume', calc: true },
    { name: 'remarks_ltr',                  label: 'Remarks', type: 'text' },
  ],
  nos: [
    { name: 'gross_weight',                           label: 'Gross Weight (kgs)' },
    { name: 'no_of_bowl_icebox',                      label: 'Total Bowls (nos)' },
    { name: 'weight_of_1_roti',                       label: '1 Roti (gms)' },
    { name: 'weight_of_1_empty_bowl_icebox',          label: '1 Bowl Empty (kgs)' },
    { name: 'total_weight_of_empty_bowl_icebox',      label: 'Empty Weight (kgs)', calc: true },
    { name: 'total_net_weight_of_filled_bowl_icebox', label: 'Net Weight (kgs)', calc: true },
    { name: 'net_weight_of_filled_bowl_icebox',       label: '1 Bowl Net (kgs)', calc: true },
    { name: 'no_of_rotis_in_1_bowl_icebox',           label: '1 Bowl Rotis (nos)', calc: true, highlight: true },
    { name: 'remarks_nos',                            label: 'Remarks', type: 'text' },
  ],
};

// One row height for a label and for the box it names, so the two columns line up whatever the
// family shows. The old screen spaced its labels by hand -- mt-5, mt-7, mt-9 -- and a field added
// or dropped anywhere above slid every label below it off its box.
const ROW = "h-16 flex items-center";

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) || !isFinite(n) ? 0 : n;
};
const fixed = (n) => (isNaN(n) || !isFinite(n) ? "0.00" : n.toFixed(2));

const blankRow = (index) => ({
  batch: index + 1,
  bowl_type: "",
  gross_weight: "",
  no_of_bowl_icebox: "",
  weight_of_1_roti: "",
  weight_of_1_empty_bowl_icebox: "",
  total_weight_of_empty_bowl_icebox: "",
  total_net_weight_of_filled_bowl_icebox: '',
  net_weight_of_filled_bowl_icebox: "",
  no_of_rotis_in_1_bowl_icebox: "",
  remarks_nos: "",
  bottom_diameter_d1: "",
  diameter_of_filled_height_d2: "",
  total_height_of_the_bowl: "",
  empty_height: "",
  filled_height: "",
  // Height Selection and Height are the screen's own: a bowl is measured the way Finished Goods
  // measures a vessel. They are not columns of bowl_screen, so what is stored is what they work
  // out -- filled_height and empty_height, as before -- and a saved batch comes back as the
  // filled height it amounts to. The volume is the same either way; only which of the two was
  // typed is not remembered.
  height_selection: { value: 1, label: "Filled Height" },
  height: "",
  sensor_volume: '',
  manual_volume: "",
  avg_volume_of_1_filled_bowl: "",
  remarks_ltr: "",
  no_of_bowls_for_measurement: "",
  weight_of_1_empty_bowl: "",
  total_empty_weight: '',
  filled_weight: '',
  net_fg_weight: '',
  avg_weight_of_1_filled_bowl: "",
  remarks_kgs: "",
});

const blankForm = () => Array.from({ length: batchSize }, (_, index) => blankRow(index));

// Everything that is worked out rather than typed, for every batch at once.
//
// It runs over the whole list because a batch nobody has touched still has figures to show: a
// bowl type and a bowl count arrive by default, and those two already say what the empty weight
// is. The version before this one only ever recomputed the batch last typed into.
const withDerived = (rows, method) => {
  const family = familyOf(method);
  if (!family) return rows;

  if (family === 'kgs') {
    const next = rows.map((row) => {
      const bowls = num(row.no_of_bowls_for_measurement);
      const total_empty_weight = fixed(bowls * num(row.weight_of_1_empty_bowl));
      const net_fg_weight = fixed(num(row.filled_weight) - num(total_empty_weight));
      return { ...row, total_empty_weight, net_fg_weight };
    });
    // 1 Bowl Net: all the net weight there is, over all the bowls it was measured over.
    const weight_sum = next.reduce((a, r) => a + num(r.net_fg_weight), 0);
    const bowl_sum = next.reduce((a, r) => a + num(r.no_of_bowls_for_measurement), 0);
    const avg = bowl_sum > 0 ? fixed(weight_sum / bowl_sum) : "";
    return next.map((row) => ({ ...row, avg_weight_of_1_filled_bowl: avg }));
  }

  if (family === 'ltr') {
    const next = rows.map((row) => {
      // Filled Height is the height as typed; Empty Height is what is left above it.
      const typed = num(row.height);
      const total = num(row.total_height_of_the_bowl);
      const filled = row.height_selection?.value == 2 ? total - typed : typed;
      const radius = num(row.diameter_of_filled_height_d2) / 2;
      const manual_volume = fixed((3.14 * radius * radius * filled) / 1000);
      return {
        ...row,
        manual_volume,
        // What bowl_screen keeps: the two heights this one height amounts to.
        filled_height: fixed(filled),
        empty_height: fixed(total - filled),
      };
    });
    // A batch measured by sensor is worth its sensor reading; the rest are worth what the
    // measurements say. Sensor Volume used to be recorded and then ignored.
    const volumes = next
      .map((r) => (num(r.sensor_volume) > 0 ? num(r.sensor_volume) : num(r.manual_volume)))
      .filter((v) => v > 0);
    const avg = volumes.length ? fixed(volumes.reduce((a, v) => a + v, 0) / volumes.length) : "";
    return next.map((row) => ({ ...row, avg_volume_of_1_filled_bowl: avg }));
  }

  return rows.map((row) => {
    const bowls = num(row.no_of_bowl_icebox);
    const total_weight_of_empty_bowl_icebox = fixed(bowls * num(row.weight_of_1_empty_bowl_icebox));
    const total_net_weight_of_filled_bowl_icebox =
      fixed(num(row.gross_weight) - num(total_weight_of_empty_bowl_icebox));
    const net_weight_of_filled_bowl_icebox =
      bowls > 0 ? fixed(num(total_net_weight_of_filled_bowl_icebox) / bowls) : "0.00";
    // 1 Roti is grams and the bowl is kilos, so the thousand is the two units meeting.
    const roti_gms = num(row.weight_of_1_roti);
    const no_of_rotis_in_1_bowl_icebox =
      roti_gms > 0 ? fixed((num(net_weight_of_filled_bowl_icebox) / roti_gms) * 1000) : "0.00";
    return {
      ...row,
      total_weight_of_empty_bowl_icebox,
      total_net_weight_of_filled_bowl_icebox,
      net_weight_of_filled_bowl_icebox,
      no_of_rotis_in_1_bowl_icebox,
    };
  });
};

// The figures shown once under the batches rather than in every column, since one of them is all
// there is: they are read off every batch together.
const summaryOf = (rows, method) => {
  const family = familyOf(method);
  if (family === 'kgs') {
    return [{
      name: 'avg_weight_of_1_filled_bowl',
      label: '1 Bowl Net (kgs)',
      value: rows?.[0]?.avg_weight_of_1_filled_bowl ?? "",
    }];
  }
  if (family === 'ltr') {
    return [{
      name: 'avg_volume_of_1_filled_bowl',
      label: '1 Bowl Avg. (ltrs)',
      value: rows?.[0]?.avg_volume_of_1_filled_bowl ?? "",
    }];
  }
  if (family === 'nos') {
    // A batch counts only when it has both figures: a bowl weighed with no roti weight beside it
    // says nothing about how many rotis were in it, and neither does the other way round.
    const measured = rows.filter(
      (r) => num(r.net_weight_of_filled_bowl_icebox) > 0 && num(r.weight_of_1_roti) > 0
    );
    const net_sum = measured.reduce((a, r) => a + num(r.net_weight_of_filled_bowl_icebox), 0);
    const roti_sum = measured.reduce((a, r) => a + num(r.weight_of_1_roti), 0);
    return [
      {
        name: 'avg_bowl_weight',
        label: '1 Bowl Avg. Weight (kgs)',
        value: measured.length ? fixed(net_sum / measured.length) : "",
      },
      {
        name: 'avg_bowl_rotis',
        label: '1 Bowl Avg. Rotis (nos)',
        value: roti_sum > 0 ? fixed((net_sum / roti_sum) * 1000) : "",
      },
    ];
  }
  return [];
};

function BowlScreen() {
  const today = new Date().toISOString().split("T")[0];
  const [mealList, setMealList] = useState([]);
  const [methodList, setMethodList] = useState([]);
  const [methodType, setMethodType] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const [bowlList, setBowlList] = useState([]);
  const [date, setDate] = useState(today);
  const [session, setSession] = useState("");
  const [formData, setFormData] = useState(blankForm());

  useEffect(() => {
    getBowlList();
    getMethodList();
  }, []);

  useEffect(() => {
    setFormData(blankForm());
  }, [batchSize]);

  useEffect(() => {
    if (mealList.length > 0 && activeIndex != null) {
      const item = mealList.find((item, index) => index === activeIndex) ?? null;
      const itemId = item.menu_item_id ?? null;
      const method = item?.measurements?.id ?? item.method;
      setMethodType(method);
      const fetchDetail = async () => {
        const condition = `date = '${date}' AND session = '${session.value}' AND menu_item_id = '${itemId}' AND method = '${method}'`;
        const response = await axios.get(
          `${baseUrl}/comman/list?table=bowl_screen&condition=${condition}`
        );

        // A Nos. batch weighs its rotis on the Finished Goods screen, so 1 Roti (gms) is that
        // screen's Finished roti weight (gms.) for the same batch. Only a starting point: the box
        // can be typed into, and anything already recorded here stands.
        let fgRows = [];
        if (familyOf(method) === 'nos') {
          // Rotis are weighed under the Nos. methods there too, and an item measured more
          // than one way has Finished Goods rows that are nothing to do with rotis.
          const fgCondition = `menu_item_id = '${itemId}' AND date = '${date}' AND session = '${session.value}' AND (method = 3 OR method = 6)`;
          const fgResponse = await axios.get(
            `${baseUrl}/comman/list?table=finished_goods&condition=${fgCondition}`
          );
          fgRows = fgResponse.data.data ?? [];
        }

        getDetailDispaly(response.data.data, method, fgRows);
      };
      fetchDetail();
    }
  }, [mealList, activeIndex]);

  // The bowl a batch is measured in, and everything the bowl master already knows about it.
  //
  // Choosing a bowl says what its measurements are, so they are written over whatever was there.
  // A bowl arriving by default says the same, but must not talk over a figure someone recorded:
  // keepExisting leaves anything already filled in alone.
  const applyBowl = (row, bowlItem, family, keepExisting = false) => {
    if (!bowlItem) return row;
    const next = { ...row, bowl_type: { value: bowlItem.id, label: bowlItem.name } };
    const put = (field, value) => {
      if (keepExisting && next[field] !== "" && next[field] != null) return;
      next[field] = value ?? 0;
    };
    if (family === 'kgs') {
      put('weight_of_1_empty_bowl', bowlItem.bowl_weight_empty);
    } else if (family === 'ltr') {
      put('bottom_diameter_d1', bowlItem.bowl_diameter_bottom);
      put('diameter_of_filled_height_d2', bowlItem.bowl_diameter_marked);
      put('total_height_of_the_bowl', bowlItem.bowl_total_height);
    } else if (family === 'nos') {
      put('weight_of_1_empty_bowl_icebox', bowlItem.bowl_weight_empty);
    }
    return next;
  };

  // What a batch starts on before anyone has recorded anything: a bowl and, where the family
  // measures over several of them, three of them. A batch that already has a bowl keeps it.
  const applyDefaults = (rows, method) => {
    const family = familyOf(method);
    if (!family) return rows;
    const defaultBowl = bowlList.find(
      (b) => String(b.name).trim().toUpperCase() === DEFAULT_BOWL[family]
    );
    return rows.map((row) => {
      let next = row.bowl_type?.value ? row : applyBowl(row, defaultBowl, family, true);
      if (family === 'kgs' && next.no_of_bowls_for_measurement === "") {
        next = { ...next, no_of_bowls_for_measurement: DEFAULT_BOWL_COUNT };
      }
      if (family === 'nos' && next.no_of_bowl_icebox === "") {
        next = { ...next, no_of_bowl_icebox: DEFAULT_BOWL_COUNT };
      }
      return next;
    });
  };

  const getDetailDispaly = async (detail, method, fgRows = []) => {
    try {
      const rows = blankForm().map((initialItem) => {
        const bowlObject = (detail ?? []).find((object) => object.batch == initialItem.batch);
        if (bowlObject) {
          const bowlItem = bowlList.find((item) => item.id == bowlObject.bowl_type);
          initialItem.bowl_type = bowlItem
            ? { value: bowlItem.id, label: bowlItem.name }
            : { value: "", label: "" };

          if (method == 1 || method == 4) {
            initialItem.no_of_bowls_for_measurement = bowlObject.no_of_bowls_for_measurement;
            initialItem.weight_of_1_empty_bowl = bowlObject.weight_of_1_empty_bowl;
            initialItem.total_empty_weight = bowlObject.total_empty_weight;
            initialItem.filled_weight = bowlObject.filled_weight;
            initialItem.net_fg_weight = bowlObject.net_fg_weight;
            initialItem.avg_weight_of_1_filled_bowl = bowlObject.avg_weight_of_1_filled_bowl;
            initialItem.remarks_kgs = bowlObject.remarks_kgs;
          }

          if (method == 2 || method == 5) {
            initialItem.bottom_diameter_d1 = bowlObject.bottom_diameter_d1;
            initialItem.diameter_of_filled_height_d2 = bowlObject.diameter_of_filled_height_d2;
            initialItem.total_height_of_the_bowl = bowlObject.total_height_of_the_bowl;
            initialItem.empty_height = bowlObject.empty_height;
            initialItem.filled_height = bowlObject.filled_height;
            // Saved as two heights, read back as the filled one -- which is the default choice,
            // and the height that makes the volume it made before.
            initialItem.height_selection = { value: 1, label: "Filled Height" };
            initialItem.height = bowlObject.filled_height;
            initialItem.sensor_volume = bowlObject.sensor_volume;
            initialItem.manual_volume = bowlObject.manual_volume;
            initialItem.avg_volume_of_1_filled_bowl = bowlObject.avg_volume_of_1_filled_bowl;
            initialItem.remarks_ltr = bowlObject.remarks_ltr;
          }

          if (method == 3 || method == 6) {
            initialItem.gross_weight = bowlObject.gross_weight;
            initialItem.no_of_bowl_icebox = bowlObject.no_of_bowl_icebox;
            initialItem.weight_of_1_roti = bowlObject.weight_of_1_roti;
            initialItem.weight_of_1_empty_bowl_icebox = bowlObject.weight_of_1_empty_bowl_icebox;
            initialItem.total_weight_of_empty_bowl_icebox = bowlObject.total_weight_of_empty_bowl_icebox;
            initialItem.total_net_weight_of_filled_bowl_icebox = bowlObject.total_net_weight_of_filled_bowl_icebox;
            initialItem.net_weight_of_filled_bowl_icebox = bowlObject.net_weight_of_filled_bowl_icebox;
            initialItem.no_of_rotis_in_1_bowl_icebox = bowlObject.no_of_rotis_in_1_bowl_icebox;
            initialItem.remarks_nos = bowlObject.remarks_nos;
          }
        }

        if (familyOf(method) === 'nos' && !num(initialItem.weight_of_1_roti)) {
          const fgRow = fgRows.find(
            (f) => f.batch == initialItem.batch && num(f.avg_finished_roti_weight) > 0
          );
          if (fgRow) {
            initialItem.weight_of_1_roti = fgRow.avg_finished_roti_weight;
          }
        }

        return { ...initialItem };
      });

      setFormData(withDerived(applyDefaults(rows, method), method));
    } catch (e) {
      console.log(e);
    }
  };

  const handleClick = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const getBowlList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=bowl_master&condition=status=1`
    );
    setBowlList(response.data.data);
  };

  const getMethodList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=method_master&condition=status=1`
    );
    setMethodList(response.data.data);
  };

  const handelFilterChange = async (date, session) => {
    setDate(date);
    setSession(session);
    if (date && session) {
      const condition = ` m.track_for_report = 1 AND m.session='${session.value}' AND m.date='${date}'`;
      const response = await axios.get(
        `${baseUrl}/meal_master/item_list?condition=${condition}`
      );
      const newData = response.data.data.reduce((acc, obj) => {
        const measurementsArray = obj?.measurements?.split(",") ?? [obj.method];
        measurementsArray.filter((m) => m && m !== "0" && m !== 0).forEach((measurement) => {
          acc.push({
            ...obj,
            measurements: methodList.find((v) => v.id == measurement) ?? {},
          });
        });
        return acc;
      }, []);
      setMealList(newData);
    }
  };

  const handleInputChange = (index) => (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[index] = { ...updatedFormData[index], [name]: value };
      return withDerived(updatedFormData, methodType);
    });
  };

  const handleSelectChange = async (index, data, option) => {
    setFormData((prevFormData) => {
      const updatedFormData = [...prevFormData];
      if (option.name === 'bowl_type') {
        const bowlItem = bowlList.find((item) => item.id == data.value);
        updatedFormData[index] = applyBowl(updatedFormData[index], bowlItem, familyOf(methodType));
      } else {
        updatedFormData[index] = { ...updatedFormData[index], [option.name]: data };
      }
      return withDerived(updatedFormData, methodType);
    });
  };

  // One Save for the item, because one of these screens is one measurement taken over several
  // batches: every batch goes up, in order, under the same date, session and method.
  const handleSave = async (e) => {
    e.preventDefault();
    const menu_item_id = mealList.find((item, i) => i == activeIndex)?.menu_item_id ?? 0;
    // Meal Master's Avg Qty in 1 Bowl is one figure for the item, and for Kgs and Ltrs every
    // batch already carries it. Nos. does not -- each batch holds its own rotis -- so the one
    // read off all of them travels with the save; see pushBowlQtyToMealMaster.
    const avg_rotis_in_1_bowl =
      summaryOf(formData, methodType).find((s) => s.name === 'avg_bowl_rotis')?.value ?? "";
    try {
      for (const row of formData) {
        await axios.post(`${baseUrl}/bowl_screen/batch`, {
          formData: { ...row, avg_rotis_in_1_bowl },
          date: date,
          session: session,
          methodType: methodType,
          menu_item_id: menu_item_id,
        });
      }
      toast.success('Data successfully saved!');
    } catch (error) {
      toast.error('Failed to submit the form.');
      console.log(error);
    }
  };

  const fieldsFor = (method) => (FIELDS[familyOf(method)] ?? []).filter((f) => !f.hidden);

  return (
    <div className="">
      <div className="flex justify-between items-center py-4 px-6">
        <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Bowl</h1>
        <Toaster position="top-center" reverseOrder={false} />
      </div>
      <div className="relative  shadow-md sm:rounded-lg">
        <DateSessionFilter handleFilterChange={handelFilterChange} />
        <div>
          {mealList.map((item, index) => {
            const method = item?.measurements?.id;
            const fields = fieldsFor(method);
            return (
              <div key={index} className="border-b mt-4">
                <div
                  className="flex justify-between items-center p-4 cursor-pointer accordion-title bg-orange-400 border rounded"
                  onClick={() => handleClick(index)}
                >
                  <span>
                    <ItemNameStacked item={item} /> (
                    {item.measurements.name})
                  </span>
                  {index === activeIndex ? (
                    <ChevronUpIcon className="w-6 h-6" />
                  ) : (
                    <ChevronDownIcon className="w-6 h-6" />
                  )}
                </div>
                {index === activeIndex && (
                  <div className=" p-4 bg-gray-100">
                    <div className="flex overflow-x-auto">
                      <div className="flex mt-10 flex-col whitespace-nowrap">
                        <div className={`font-bold ${ROW}`}>Batch</div>
                        <div className={`font-bold ${ROW}`}>Bowl Type</div>
                        {fields.map((field) => (
                          <div key={field.name} className={`font-bold ${ROW}`}>{field.label}</div>
                        ))}
                      </div>
                      {Array.from({ length: batchSize }, (_, batch) => (
                        <div key={batch} className="mb-4 mt-10 ms-2 flex-shrink-0 w-52">
                          <div className={`border p-2 ${ROW}`}>{batch + 1}</div>
                          <div className={`border p-2 ${ROW}`}>
                            <div className="w-full">
                              <Select
                                {...menuAboveTable}
                                classNamePrefix="select"
                                styles={withMenuOnTop({
                                  placeholder: (provided) => ({
                                    ...provided,
                                    fontSize: "0.80rem",
                                  }),
                                  control: (provided) => ({
                                    ...provided,
                                    backgroundColor: '#FFFAF0',
                                    fontSize: '16px',
                                  }),
                                })}
                                name="bowl_type"
                                value={formData?.[batch]?.bowl_type}
                                onChange={(selectedOption, name) => {
                                  handleSelectChange(batch, selectedOption, name);
                                }}
                                placeholder="Bowl Type"
                                options={bowlList.map((bowl) => ({
                                  value: bowl.id,
                                  label: bowl.name,
                                }))}
                                filterOption={(option, inputValue) => {
                                  return option.data.label
                                    .toLowerCase()
                                    .includes(inputValue.toLowerCase());
                                }}
                              />
                            </div>
                          </div>
                          {fields.map((field) => (
                            <div key={field.name} className={`border p-2 ${ROW}`}>
                              {field.type === 'select' ? (
                                <div className="w-full">
                                  <Select
                                    {...menuAboveTable}
                                    classNamePrefix="select"
                                    styles={withMenuOnTop({
                                      placeholder: (provided) => ({ ...provided, fontSize: "0.80rem" }),
                                      control: (provided) => ({ ...provided, backgroundColor: '#FFFAF0', fontSize: '16px' }),
                                    })}
                                    name={field.name}
                                    value={formData?.[batch]?.[field.name]}
                                    onChange={(selectedOption, name) => {
                                      handleSelectChange(batch, selectedOption, name);
                                    }}
                                    placeholder={field.label}
                                    options={field.options}
                                  />
                                </div>
                              ) : (
                                <input
                                  type={field.type === 'text' ? 'text' : 'number'}
                                  step={field.type === 'text' ? undefined : "0.01"}
                                  id={field.name}
                                  name={field.name}
                                  placeholder={field.label}
                                  disabled={field.calc}
                                  value={formData?.[batch]?.[field.name] ?? ""}
                                  onChange={handleInputChange(batch)}
                                  onWheel={(e) => e.target.blur()}
                                  className={`w-full border rounded-md placeholder:text-sm p-1 ${
                                    field.highlight ? 'bg-lime-300' : field.calc ? '' : 'bg-orange-50'
                                  }`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    {/* Read off every batch together, so shown once under them rather than
                        repeated down each column -- and one Save for the item, not one per batch. */}
                    <div className="flex flex-wrap items-end gap-4 mt-4">
                      {summaryOf(formData, method).map((summary) => (
                        <div key={summary.name}>
                          <div className="font-bold mb-1">{summary.label}</div>
                          <input
                            type="number"
                            step="0.01"
                            id={summary.name}
                            name={summary.name}
                            placeholder={summary.label}
                            disabled
                            readOnly
                            value={summary.value ?? ""}
                            className="w-52 border rounded-md placeholder:text-sm p-1 bg-lime-300"
                          />
                        </div>
                      ))}
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-green-500 text-white rounded-md placeholder:text-sm"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default BowlScreen;
